import { Injectable, Injector } from "@angular/core";
import { ApplicationInsights, ITelemetryItem, SeverityLevel } from "@microsoft/applicationinsights-web";
import { filter } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { AuthService } from "@Services/auth.service";

@Injectable()
export class LoggingService {

    appInsights: ApplicationInsights = null;
    lastHttpProtocol: string = null;

    private protocolMap = new Map<string, string>();
    private static readonly PROTOCOL_MAP_MAX = 500;

    constructor(private injector: Injector) {
        this.startProtocolObserver();

        if (environment.applicationInsights) {
            this.appInsights = new ApplicationInsights({
                config: {
                    connectionString: environment.applicationInsights,
                    enableAutoRouteTracking: true // option to log all route changes
                }
            });

            this.appInsights.loadAppInsights();
            this.addTelemetryInitializer();
            this.trackAuthenticatedUser();
        }
    }

    private _authService: AuthService;

    private get authService(): AuthService {
        if (!this._authService) {
            try {
                this._authService =
                    this.injector.get(AuthService);
            } catch {
                return null;
            }
        }
        return this._authService;
    }

    private startProtocolObserver(): void {
        if (typeof PerformanceObserver === 'undefined') {
            return;
        }
        try {
            const observer = new PerformanceObserver((list) => {
                for (const entry of list.getEntries() as PerformanceResourceTiming[]) {
                    const protocol = entry.nextHopProtocol;
                    if (protocol) {
                        this.protocolMap.set(entry.name, protocol);
                        this.lastHttpProtocol = protocol;
                    }
                }
                if (this.protocolMap.size > LoggingService.PROTOCOL_MAP_MAX) {
                    const excess = this.protocolMap.size - LoggingService.PROTOCOL_MAP_MAX;
                    const keys = this.protocolMap.keys();
                    for (let i = 0; i < excess; i++) {
                        this.protocolMap.delete(keys.next().value);
                    }
                }
            });
            observer.observe({ type: 'resource', buffered: true });
        } catch {
            // PerformanceObserver not supported for 'resource' type
        }
    }

    getProtocol(url: string): string | null {
        if (this.protocolMap.has(url)) {
            return this.protocolMap.get(url);
        }
        for (const [name, protocol] of this.protocolMap) {
            if (name.endsWith(url) || url.endsWith(name)) {
                return protocol;
            }
        }
        return null;
    }

    private addTelemetryInitializer(): void {
        this.appInsights.addTelemetryInitializer(
            (item: ITelemetryItem) => {
                const props = item.data = item.data || {};
                props['application'] = 'FrontEnd';
                const auth = this.authService;
                if (auth) {
                    const tenantKey = auth.getTenantKey();
                    if (tenantKey) {
                        props['TenantKey'] = tenantKey;
                    }
                    const areaKey = auth.getAreaKey();
                    if (areaKey) {
                        props['AreaKey'] = areaKey;
                    }
                    const userId = auth.getIdentityKey();
                    if (userId) {
                        props['UserId'] = userId;
                    }
                }
                if (this.lastHttpProtocol) {
                    props['HttpProtocol'] = this.lastHttpProtocol;
                }
            }
        );
    }

    private trackAuthenticatedUser(): void {
        Promise.resolve().then(() => {
            const auth = this.authService;
            if (!auth) {
                return;
            }
            auth.authContext$.pipe(
                filter(ctx => !!ctx?.identityKey)
            ).subscribe(ctx => {
                this.appInsights.setAuthenticatedUserContext(
                    ctx.identityKey, ctx.tenantKey, true
                );
            });
        });
    }

    logPageView(name?: string, url?: string, prevUrl?: string) { // option to call manually
        this.appInsights?.trackPageView({
            name: name,
            uri: url,
            refUri: prevUrl
        });
    }

    logEvent(name: string, properties?: { [key: string]: any }) {
        this.writeOutput(console.info, name, properties);
        this.appInsights?.trackEvent({ name: name }, properties);
    }

    logMetric(name: string, average: number, properties?: { [key: string]: any }) {
        this.appInsights?.trackMetric({ name: name, average: average }, properties);
    }

    logException(exception: Error,
        severityLevel: SeverityLevel | number = SeverityLevel.Error,
        data?: { [key: string]: any }) {

        this.writeOutput(console.error, exception, data);
        // Ensure custom properties are included with the exception telemetry
        this.appInsights?.trackException({ exception: exception, severityLevel: severityLevel }, data);
    }

    logWarn(message: string, properties?: { [key: string]: any }) {
        this.writeOutput(console.warn, message, properties);
        this.appInsights.trackTrace({ message: message, severityLevel: SeverityLevel.Warning }, properties)
    }

    logInfo(message: string, properties?: { [key: string]: any }) {
        this.writeOutput(console.info, message, properties);
        this.appInsights?.trackTrace({ message: message, severityLevel: SeverityLevel.Information }, properties);
    }

    logVerbose(message: string, properties?: { [key: string]: any }) {
        this.writeOutput(console.log, message, properties);
        this.appInsights.trackTrace({ message: message, severityLevel: SeverityLevel.Verbose }, properties)
    }

    private writeOutput(func: Function, message: string | Error, properties?: { [key: string]: any }) {
        if (properties) {
            func(message, properties);
        } else {
            func(message);
        }
    }
}